import React from 'react';
import { hintStyle, labelStyle, statusChipStyle } from './settingsStyles';
import type { ReactNode } from 'react';

/** Shared Settings connection acknowledgment surface (GitHub PAT + Jira OAuth/token). */
export const connectionAckBannerStyle: React.CSSProperties = {
  display: 'flex',
  flexDirection: 'column',
  gap: 4,
  padding: '12px 14px',
  borderRadius: 12,
  background: 'var(--z-ok-soft, #dcfce7)',
  color: 'var(--z-ok, #166534)',
  border: '1px solid color-mix(in srgb, var(--z-ok, #166534) 18%, transparent)',
};

export interface SettingsConnectionBannerModel {
  title: string;
  detail: string;
}

export interface SettingsConnectionStatusProps {
  /** Prefix for data-testid: `{prefix}-status-chip`, `{prefix}-connection-ack`. */
  testIdPrefix: string;
  /** Section title (e.g. GitHub, Jira). */
  title: string;
  chipLabel: string;
  chipOk: boolean;
  hint: string;
  banner?: SettingsConnectionBannerModel | null;
  children?: React.ReactNode;
  icon?: ReactNode;
  headerAction?: ReactNode;
  compact?: boolean;
}

/**
 * Common connection header for Settings integration sections:
 * title + status chip + hint + optional success acknowledgment banner.
 */
export const SettingsConnectionStatus: React.FC<SettingsConnectionStatusProps> = ({
  testIdPrefix,
  title,
  chipLabel,
  chipOk,
  hint,
  banner = null,
  children,
  icon,
  headerAction,
  compact = false,
}) => (
  <>
    {!compact ? (
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: 10,
          minHeight: 34,
        }}
      >
        {icon}
        <div style={{ ...labelStyle, fontSize: 15, color: 'var(--z-text)' }}>{title}</div>
        <span data-testid={`${testIdPrefix}-status-chip`} style={statusChipStyle(chipOk)}>
          <span aria-hidden="true" style={{ fontSize: 16, lineHeight: 0 }}>
            {chipOk ? '•' : '•'}
          </span>
          {chipLabel}
        </span>
        <div style={{ marginLeft: 'auto' }}>{headerAction}</div>
      </div>
    ) : null}
    <p style={hintStyle}>{hint}</p>
    {banner ? (
      <div
        data-testid={`${testIdPrefix}-connection-ack`}
        style={connectionAckBannerStyle}
        role="status"
      >
        <strong style={{ fontSize: 13, fontWeight: 700 }}>{banner.title}</strong>
        <span style={{ fontSize: 12, lineHeight: 1.45, opacity: 0.92 }}>{banner.detail}</span>
      </div>
    ) : null}
    {children}
  </>
);
